import type { operations } from "@airange/generated-contracts/edu";
import { eduAdminOpenApiClient } from "../generated/client";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult } from "./openapi-result";

type OperationResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends {
  content: { "application/json": infer TResponse };
}
  ? TResponse
  : never;

type OperationRequestBody<TOperation extends { requestBody?: unknown }> = NonNullable<
  TOperation["requestBody"]
> extends {
  content: { "application/json": infer TBody };
}
  ? TBody
  : never;

type OperationQuery<TOperation extends { parameters: { query?: unknown } }> = NonNullable<
  TOperation["parameters"]["query"]
>;

type OperationPath<TOperation extends { parameters: { path?: unknown } }> = NonNullable<
  TOperation["parameters"]["path"]
>;

type UiNonNullQuery<TQuery extends object, TReplaced extends keyof TQuery = never> = {
  [TKey in Exclude<keyof TQuery, TReplaced>]?: NonNullable<TQuery[TKey]>;
};

type GetPublicCoursesOverviewOperation =
  operations["get_public_courses_overview_api_public_courses_overview_get"];
type GetPublicCoursesOperation = operations["get_public_courses_api_public_courses_get"];
type CreatePublicCourseOperation = operations["post_public_course_api_public_courses_post"];
type GetPublicCourseOperation = operations["get_public_course_api_public_courses__public_course_id__get"];
type UpdatePublicCourseOperation = operations["patch_public_course_api_public_courses__public_course_id__patch"];
type GetPublicCourseNoticesOperation =
  operations["get_public_course_notices_api_public_courses__public_course_id__notices_get"];
type CreatePublicCourseNoticeOperation =
  operations["post_public_course_notice_api_public_courses__public_course_id__notices_post"];
type GetPublicCourseBoardThreadsOperation =
  operations["get_public_course_board_threads_api_public_courses__public_course_id__board_threads_get"];
type CreatePublicCourseBoardThreadOperation =
  operations["post_public_course_board_thread_api_public_courses__public_course_id__board_threads_post"];
type PublishPublicCourseOperation =
  operations["post_public_course_publish_api_public_courses__public_course_id__publish_post"];
type PausePublicCourseOperation = operations["post_public_course_pause_api_public_courses__public_course_id__pause_post"];
type SyncPublicCourseOperation = operations["post_public_course_sync_api_public_courses__public_course_id__sync_post"];

type PublicCourseId = OperationPath<GetPublicCourseOperation>["public_course_id"];
type PublicCoursesGeneratedQuery = OperationQuery<GetPublicCoursesOperation>;

export type EduPublicCoursesListQuery = UiNonNullQuery<PublicCoursesGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<PublicCoursesGeneratedQuery["page_size"]>;
};
export type EduPublicCoursesOverview = OperationResponse<GetPublicCoursesOverviewOperation, 200>;
export type EduPublicCourseOverviewBucket = EduPublicCoursesOverview["buckets"][number];
export type EduPublicCoursesListResponse = OperationResponse<GetPublicCoursesOperation, 200>;
export type EduPublicCourseListItem = EduPublicCoursesListResponse["items"][number];
export type EduPublicCourseSourceType = EduPublicCourseListItem["source_type"];
export type EduPublicCoursePublishStatus = EduPublicCourseListItem["publish_status"];
export type EduPublicCourseCreatePayload = OperationRequestBody<CreatePublicCourseOperation>;
export type EduPublicCourseDetail = OperationResponse<GetPublicCourseOperation, 200>;
export type EduPublicCourseUpdatePayload = OperationRequestBody<UpdatePublicCourseOperation>;
export type EduPublicCourseNoticesResponse = OperationResponse<GetPublicCourseNoticesOperation, 200>;
export type EduPublicCourseNoticeItem = EduPublicCourseNoticesResponse["items"][number];
export type EduPublicCourseNoticeSourceKind = EduPublicCourseNoticeItem["source_kind"];
export type EduPublicCourseNoticeCreatePayload = OperationRequestBody<CreatePublicCourseNoticeOperation>;
export type EduPublicCourseBoardThreadsResponse = OperationResponse<GetPublicCourseBoardThreadsOperation, 200>;
export type EduPublicCourseBoardThreadItem = EduPublicCourseBoardThreadsResponse["items"][number];
export type EduPublicCourseBoardThreadCreatePayload = OperationRequestBody<CreatePublicCourseBoardThreadOperation>;
export type EduPublicCourseOutlineSection = EduPublicCourseDetail["sections"][number];
type PublishPublicCourseResponse = OperationResponse<PublishPublicCourseOperation, 200>;
type PausePublicCourseResponse = OperationResponse<PausePublicCourseOperation, 200>;
type SyncPublicCourseResponse = OperationResponse<SyncPublicCourseOperation, 200>;

function toPublicCoursesListQueryParams(
  query?: EduPublicCoursesListQuery,
): PublicCoursesGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<PublicCoursesGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

const eduPublicCoursesApi = {
  async getPublicCoursesOverview(): Promise<EduPublicCoursesOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/public-courses/overview"));
  },
  async getPublicCourses(query?: EduPublicCoursesListQuery): Promise<EduPublicCoursesListResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/public-courses", {
        params: { query: toPublicCoursesListQueryParams(query) },
      }),
    );
  },
  async createPublicCourse(payload: EduPublicCourseCreatePayload): Promise<EduPublicCourseListItem> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.POST("/api/public-courses", { body: payload }));
  },
  async getPublicCourseDetail(publicCourseId: PublicCourseId): Promise<EduPublicCourseDetail> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/public-courses/{public_course_id}", {
        params: { path: { public_course_id: publicCourseId } },
      }),
    );
  },
  async updatePublicCourse(
    publicCourseId: PublicCourseId,
    payload: EduPublicCourseUpdatePayload,
  ): Promise<EduPublicCourseDetail> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PATCH("/api/public-courses/{public_course_id}", {
        params: { path: { public_course_id: publicCourseId } },
        body: payload,
      }),
    );
  },
  async getPublicCourseNotices(publicCourseId: PublicCourseId): Promise<EduPublicCourseNoticesResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/public-courses/{public_course_id}/notices", {
        params: { path: { public_course_id: publicCourseId } },
      }),
    );
  },
  async createPublicCourseNotice(
    publicCourseId: PublicCourseId,
    payload: EduPublicCourseNoticeCreatePayload,
  ): Promise<EduPublicCourseNoticesResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/public-courses/{public_course_id}/notices", {
        params: { path: { public_course_id: publicCourseId } },
        body: payload,
      }),
    );
  },
  async getPublicCourseBoardThreads(publicCourseId: PublicCourseId): Promise<EduPublicCourseBoardThreadsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/public-courses/{public_course_id}/board-threads", {
        params: { path: { public_course_id: publicCourseId } },
      }),
    );
  },
  async createPublicCourseBoardThread(
    publicCourseId: PublicCourseId,
    payload: EduPublicCourseBoardThreadCreatePayload,
  ): Promise<EduPublicCourseBoardThreadsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/public-courses/{public_course_id}/board-threads", {
        params: { path: { public_course_id: publicCourseId } },
        body: payload,
      }),
    );
  },
  async publishPublicCourse(publicCourseId: PublicCourseId): Promise<PublishPublicCourseResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/public-courses/{public_course_id}/publish", {
        params: { path: { public_course_id: publicCourseId } },
      }),
    );
  },
  async pausePublicCourse(publicCourseId: PublicCourseId): Promise<PausePublicCourseResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/public-courses/{public_course_id}/pause", {
        params: { path: { public_course_id: publicCourseId } },
      }),
    );
  },
  async syncPublicCourse(publicCourseId: PublicCourseId): Promise<SyncPublicCourseResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/public-courses/{public_course_id}/sync", {
        params: { path: { public_course_id: publicCourseId } },
      }),
    );
  },
};

export default eduPublicCoursesApi;
